import { cva, type VariantProps } from 'class-variance-authority';
import { forwardRef, type ButtonHTMLAttributes } from 'react';
import { Loader2 } from 'lucide-react';
import { cn } from '@/lib/utils';

const raised = 'shadow-[inset_0_1px_0_0_rgb(255_255_255/0.16),0_1px_2px_0_rgb(15_23_42/0.14)]';

const buttonVariants = cva(
  'inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium transition-[color,background-color,border-color,box-shadow,transform] duration-150 focus-ring active:scale-[0.97] disabled:pointer-events-none disabled:opacity-50 [&_svg]:shrink-0',
  {
    variants: {
      variant: {
        default: `bg-primary text-primary-foreground ${raised} hover:bg-primary/90`,
        secondary: 'border border-border bg-secondary text-secondary-foreground hover:bg-secondary/70 hover:border-muted-foreground/30',
        outline:
          'border border-border bg-background text-foreground shadow-[0_1px_2px_0_rgb(15_23_42/0.05)] hover:border-muted-foreground/40 hover:bg-muted',
        ghost: 'text-foreground hover:bg-accent hover:text-accent-foreground',
        destructive: `bg-destructive text-destructive-foreground ${raised} hover:bg-destructive/90`,
        'danger-outline':
          'border border-destructive/40 bg-background text-destructive hover:border-destructive hover:bg-destructive/10',
        success: `bg-success text-success-foreground ${raised} hover:bg-success/90`,
        link: 'text-primary underline-offset-4 hover:underline',
      },
      size: {
        default: 'h-9 px-4 py-2',
        xs: 'h-7 rounded-md px-2.5 text-xs [&_svg]:h-3.5 [&_svg]:w-3.5',
        sm: 'h-8 rounded-md px-3 text-xs',
        lg: 'h-11 rounded-md px-6 text-base',
        icon: 'h-9 w-9',
      },
    },
    defaultVariants: { variant: 'default', size: 'default' },
  },
);

export interface ButtonProps
  extends ButtonHTMLAttributes<HTMLButtonElement>, VariantProps<typeof buttonVariants> {
  loading?: boolean;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, loading, children, disabled, ...props }, ref) => (
    <button
      ref={ref}
      className={cn(buttonVariants({ variant, size }), className)}
      disabled={disabled || loading}
      {...props}
    >
      {loading && <Loader2 className="h-4 w-4 animate-spin" aria-hidden />}
      {children}
    </button>
  ),
);
Button.displayName = 'Button';
